import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ArrowRight, Settings } from 'lucide-react'
import { useDashboardStats, useHubSpotStatus, useEmailAccounts, useSequences, useDeliverabilityHealth, useReactivateEnrollments } from '@/hooks/useApi'
import { HealthBanner, RecoveryBanner } from '@/components/HealthBanner'
import { formatNumber } from '@/lib/format'
import { ViewModeSwitch } from '@/components/ViewModeSwitch'
import { useViewModeParam } from '@/hooks/useViewMode'
import type { DashboardFunnel } from '@/types/api'

const PERIODS = ['7d', '30d', '90d', 'all'] as const

type Period = '7d' | '30d' | '90d' | 'all'

export function Dashboard() {
  const { t } = useTranslation('dashboard')
  const [selectedSequenceId, setSelectedSequenceId] = useState<string | undefined>()
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('all')

  const viewMode = useViewModeParam()

  const { data: stats, isLoading: loadingStats } = useDashboardStats({
    sequenceId: selectedSequenceId,
    period: selectedPeriod,
    viewMode,
  })
  const { data: hubspotStatus, isLoading: loadingHubspot } = useHubSpotStatus()
  const { data: emailAccounts, isLoading: loadingEmail } = useEmailAccounts()
  const { data: sequences } = useSequences()
  const { data: healthData } = useDeliverabilityHealth()
  const reactivateMutation = useReactivateEnrollments()

  const isSetupComplete =
    hubspotStatus?.connected && emailAccounts && emailAccounts.length > 0

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">{t('title')}</h1>
        <ViewModeSwitch />
      </div>

      {/* Setup Alert */}
      {!loadingHubspot && !loadingEmail && !isSetupComplete && (
        <Card className="mb-8 border-orange-200 bg-orange-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-orange-800">{t('setupAlert.title')}</h3>
                <p className="text-sm text-orange-600">
                  {!hubspotStatus?.connected && t('setupAlert.connectHubspot')}
                  {!hubspotStatus?.connected &&
                    (!emailAccounts || emailAccounts.length === 0) &&
                    t('setupAlert.and')}
                  {(!emailAccounts || emailAccounts.length === 0) &&
                    t('setupAlert.connectEmail')}
                  {t('setupAlert.toStart')}
                </p>
              </div>
              <Button asChild>
                <Link to="/settings">
                  <Settings className="w-4 h-4 mr-2" />
                  {t('setupAlert.goToSettings')}
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6">
        <Select
          value={selectedSequenceId ?? 'all'}
          onValueChange={(v) => setSelectedSequenceId(v === 'all' ? undefined : v)}
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={t('filters.allSequences')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('filters.allSequences')}</SelectItem>
            {sequences?.map((seq) => (
              <SelectItem key={seq.id} value={seq.id}>
                {seq.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex gap-1">
          {PERIODS.map((value) => (
            <Button
              key={value}
              variant={selectedPeriod === value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedPeriod(value)}
            >
              {t(`filters.periods.${value}`)}
            </Button>
          ))}
        </div>
      </div>

      {/* Health Alerts */}
      {healthData && (
        <>
          <HealthBanner
            accounts={healthData}
            onReactivate={(id) => reactivateMutation.mutate(id)}
            isReactivating={reactivateMutation.isPending}
          />
          <RecoveryBanner
            accounts={healthData}
            onReactivate={(id) => reactivateMutation.mutate(id)}
            isReactivating={reactivateMutation.isPending}
          />
        </>
      )}

      {/* Metrics: Funnel + Secondary */}
      {loadingStats ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
              <CardContent className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </CardContent>
            </Card>
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}><CardContent className="py-4"><Skeleton className="h-16 w-full" /></CardContent></Card>
            ))}
          </div>
        </div>
      ) : stats ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Engagement Funnel */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>{t('funnel.title')}</CardTitle>
            </CardHeader>
            {stats.funnel.sent > 0 ? (
              <CardContent className="space-y-4">
                <FunnelRow label={t('funnel.sent')} count={stats.funnel.sent} total={stats.funnel.sent} />
                <FunnelRow label={t('funnel.delivered')} count={stats.funnel.delivered} total={stats.funnel.sent} rate={stats.rates.delivery_rate} />
                <OpenedRow funnel={stats.funnel} rate={stats.rates.open_rate} />
                <FunnelRow label={t('funnel.replied')} count={stats.funnel.replied} total={stats.funnel.sent} rate={stats.rates.reply_rate} />
              </CardContent>
            ) : (
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">{t('funnel.empty')}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('funnel.emptyHint')}
                </p>
              </CardContent>
            )}
          </Card>

          {/* Secondary Metrics */}
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('cards.bounceRate')}</p>
                <p className={`text-2xl font-bold mt-1 ${
                  stats.secondary.bounce_rate >= 5 ? 'text-red-600' :
                  stats.secondary.bounce_rate >= 3 ? 'text-yellow-600' :
                  'text-destructive'
                }`}>
                  {stats.secondary.bounce_rate}%
                </p>
                <p className="text-xs text-muted-foreground mt-1">{t('cards.bounced', { count: stats.secondary.bounced })}</p>
              </CardContent>
            </Card>
            {healthData && healthData.some(a => a.complaint_count > 0) && (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('cards.complaintRate')}</p>
                  <p className={`text-2xl font-bold mt-1 ${
                    healthData.some(a => a.complaint_rate >= 0.1) ? 'text-red-600' : 'text-foreground'
                  }`}>
                    {Math.max(...healthData.map(a => a.complaint_rate))}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('cards.complaints', { count: healthData.reduce((sum, a) => sum + a.complaint_count, 0) })}
                  </p>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('cards.activeEnrollments')}</p>
                <p className="text-2xl font-bold mt-1">{formatNumber(stats.secondary.active_enrollments)}</p>
                <p className="text-xs text-muted-foreground mt-1">{t('cards.activeEnrollmentsHint', { count: stats.secondary.active_sequences })}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('cards.activeSequences')}</p>
                <p className="text-2xl font-bold mt-1">{formatNumber(stats.secondary.active_sequences)}</p>
                <p className="text-xs text-muted-foreground mt-1">{t('cards.activeSequencesHint', { count: stats.secondary.total_sequences })}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <Card className="mb-8">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{t('noData.title')}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {t('noData.hint')}
            </p>
            <Button asChild variant="outline" className="mt-4">
              <Link to="/sequences">{t('noData.cta')}</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('actions.createSequence')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              {t('actions.createSequenceDescription')}
            </p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/sequences">
                {t('actions.goToSequences')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('actions.syncContacts')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              {t('actions.syncContactsDescription')}
            </p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/contacts">
                {t('actions.goToContacts')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('actions.viewEnrollments')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              {t('actions.viewEnrollmentsDescription')}
            </p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/enrollments">
                {t('actions.goToEnrollments')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Getting Started Guide */}
      {!isSetupComplete && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>{t('gettingStarted.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3">
              <SetupStep
                number={1}
                title={t('gettingStarted.step1Title')}
                description={t('gettingStarted.step1Description')}
                complete={hubspotStatus?.connected}
                loading={loadingHubspot}
              />
              <SetupStep
                number={2}
                title={t('gettingStarted.step2Title')}
                description={t('gettingStarted.step2Description')}
                complete={emailAccounts && emailAccounts.length > 0}
                loading={loadingEmail}
              />
              <SetupStep
                number={3}
                title={t('gettingStarted.step3Title')}
                description={t('gettingStarted.step3Description')}
                complete={stats && stats.secondary.total_contacts > 0}
                loading={loadingStats}
              />
              <SetupStep
                number={4}
                title={t('gettingStarted.step4Title')}
                description={t('gettingStarted.step4Description')}
                complete={stats && stats.secondary.active_sequences > 0}
                loading={loadingStats}
              />
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

/**
 * Riga delle aperture: il tasso è sulle sole email di cui si misura
 * l'apertura. Se nessuna email inviata è tracciata la riga lo dice, invece di
 * mostrare uno 0% che sembrerebbe "nessuno ha aperto" (#368).
 */
function OpenedRow({ funnel, rate }: { funnel: DashboardFunnel; rate: number }) {
  const { t } = useTranslation('dashboard')
  // Un'API precedente al rilascio non manda il campo: tutte tracciate, come prima
  const tracked = funnel.open_tracked ?? funnel.sent

  return (
    <div data-testid="funnel-opened">
      {tracked === 0 ? (
        <FunnelRow label={t('funnel.opened')} total={0} value={t('funnel.notTracked')} />
      ) : (
        <FunnelRow
          label={t('funnel.opened')}
          count={funnel.opened}
          total={tracked}
          rate={rate}
          hint={
            tracked < funnel.sent
              ? t('funnel.openTrackedHint', { count: tracked, formatted: formatNumber(tracked) })
              : undefined
          }
        />
      )}
    </div>
  )
}

function FunnelRow({
  label,
  count = 0,
  total,
  rate,
  value,
  hint,
}: {
  label: string
  count?: number
  total: number
  rate?: number
  /** Testo al posto di conteggio e tasso, quando il dato non è misurato */
  value?: string
  hint?: string
}) {
  const width = total > 0 ? (count / total) * 100 : 0

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-muted-foreground">{label}</span>
        {value !== undefined ? (
          <span className="text-muted-foreground">{value}</span>
        ) : (
          <span>
            <span className="font-semibold">{formatNumber(count)}</span>
            {rate !== undefined && (
              <span className="text-muted-foreground ml-2">{rate}%</span>
            )}
          </span>
        )}
      </div>
      <div className="h-6 bg-muted rounded overflow-hidden">
        <div
          className="h-full bg-primary rounded transition-all duration-500"
          style={{ width: `${width}%` }}
        />
      </div>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  )
}

function SetupStep({
  number,
  title,
  description,
  complete,
  loading,
}: {
  number: number
  title: string
  description: string
  complete?: boolean
  loading: boolean
}) {
  return (
    <li className="flex items-start gap-4">
      <div
        className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
          complete
            ? 'bg-green-100 text-green-700'
            : 'bg-muted text-muted-foreground'
        }`}
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : complete ? (
          '✓'
        ) : (
          number
        )}
      </div>
      <div>
        <p className={`font-medium ${complete ? 'text-green-700' : ''}`}>
          {title}
        </p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </li>
  )
}
